import {
    GnomeEquippedItemIdsSchema,
    type GnomeEquippedItemIds,
    type GnomeEquipmentItemId,
} from "../../schemas/slices/GnomeCustomizationSliceSchema.ts";
import { GNOME_EQUIPMENT_CATALOG } from "./gnomeCustomization.ts";
import type { QuestItemReward } from "./careerQuestRewards.ts";

/** Only registered wearables can change the local character's equipment. */
export function resolveQuestEquipmentReward(
    current: Readonly<GnomeEquippedItemIds>,
    rewards: readonly QuestItemReward[],
    earnedItemIds: readonly GnomeEquipmentItemId[],
): { equipment: GnomeEquippedItemIds; itemIds: GnomeEquipmentItemId[] } | null {
    const equipment = { ...current };
    const itemIds: GnomeEquipmentItemId[] = [];
    for (const reward of rewards) {
        const item = GNOME_EQUIPMENT_CATALOG.find(
            ({ id }) => id === reward.itemCode,
        );
        if (item === undefined || earnedItemIds.includes(item.id)) continue;
        Object.assign(equipment, { [item.category]: item.id });
        if (!itemIds.includes(item.id)) itemIds.push(item.id);
    }
    return itemIds.length === 0
        ? null
        : {
              equipment: GnomeEquippedItemIdsSchema.parse(equipment),
              itemIds,
          };
}

/** Restore only this reward's changes, preserving any subsequent wardrobe edits. */
export function undoQuestEquipmentReward(
    current: Readonly<GnomeEquippedItemIds>,
    previous: Readonly<GnomeEquippedItemIds>,
    equipped: Readonly<GnomeEquippedItemIds>,
): GnomeEquippedItemIds {
    const restored = { ...current };
    for (const slot of GnomeEquippedItemIdsSchema.keyof().options) {
        if (
            previous[slot] !== equipped[slot] &&
            current[slot] === equipped[slot]
        ) {
            Object.assign(restored, { [slot]: previous[slot] });
        }
    }
    return GnomeEquippedItemIdsSchema.parse(restored);
}

/** A quiet reward/undo must not schedule the normal outfit-change celebration. */
export function acknowledgeQuestEquipmentChange(
    observed: Readonly<GnomeEquippedItemIds>,
    before: Readonly<GnomeEquippedItemIds>,
    after: Readonly<GnomeEquippedItemIds>,
): GnomeEquippedItemIds {
    const acknowledged = { ...observed };
    for (const slot of GnomeEquippedItemIdsSchema.keyof().options) {
        if (before[slot] !== after[slot]) {
            Object.assign(acknowledged, { [slot]: after[slot] });
        }
    }
    return GnomeEquippedItemIdsSchema.parse(acknowledged);
}
